import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import * as AP from "fp-ts/Apply"
import { actions } from "xstate"
import { assertEvent } from "../../../../../_common/xstate-helpers"
import {
    isSSR,
    trackCourtesyHold,
    trackGuestsFormSubmitted,
    trackPaymentCancelled,
    trackPaymentConfirmed,
    trackPaymentError,
    trackWebQuoteFormSubmitted,
} from "../../../tracking"
import { ECommerceEvent } from "../e-commerce-machine"
import { isAtLeastCheckoutStarted } from "../e-commerce-machine-helpers"
import { ECommerceContext, StartedCheckoutState } from "../e-commerce-machine-context"

type Context = ECommerceContext
type Events = ECommerceEvent

const sequenceO = AP.sequenceS(O.Applicative)

const logError = (message: string, context?: unknown) => {
    console.error(message, context)
    if (!isSSR()) window.rollbar?.error(message, { context })
}

type PaymentTrackingData = { trackingId: string; isVsMember: boolean; checkoutStatus: StartedCheckoutState }

function paymentTrackingData(s: PaymentTrackingData) {
    return {
        trackingEvent: {
            trackingId: s.trackingId,
            isVsMember: s.isVsMember,
        },
        bookingNumber: s.checkoutStatus.response.bookingNumber,
        suitePayment: s.checkoutStatus.response.validatedPrice.suitePayment,
    }
}

export const trackingEventActions = {
    trackingEventWebQuoteSubmitted: actions.pure<Context, Events>((ctx, ev) => {
        assertEvent(ev, "WEB_QUOTE_VALIDATED")

        F.pipe(
            sequenceO({
                trackingId: O.fromNullable(ctx.quoteTrackingId),
                isVsMember: O.some(ctx.vsCode.type == "valid"),
                isTest: O.some(ev.response.isTest),
            }),
            O.fold(
                () => {
                    const context = { trackingId: ctx.quoteTrackingId, response: ev.response }
                    logError(`Invalid tracking preconditions: ${ctx.checkoutStatus.status}`, context)
                },
                (s) => {
                    trackWebQuoteFormSubmitted({ trackingId: s.trackingId, isVsMember: s.isVsMember, isTest: s.isTest })
                },
            ),
        )

        return []
    }),

    trackingEventGuestsFormSubmitted: actions.pure<Context, Events>((ctx, _ev) => {
        F.pipe(
            sequenceO({
                trackingId: O.fromNullable(ctx.checkoutTrackingId),
                isVsMember: O.some(ctx.vsCode.type == "valid"),
                checkoutStatus: O.fromPredicate(isAtLeastCheckoutStarted)(ctx.checkoutStatus),
            }),
            O.fold(
                () => {
                    const context = { trackingId: ctx.checkoutTrackingId, checkoutStatus: ctx.checkoutStatus }
                    logError(`Invalid tracking preconditions: ${ctx.checkoutStatus.status}`, context)
                },
                (s) => {
                    trackGuestsFormSubmitted(
                        {
                            trackingId: s.trackingId,
                            isVsMember: s.isVsMember,
                        },
                        s.checkoutStatus.response.bookingNumber,
                        s.checkoutStatus.response.validatedPrice.suitePayment,
                    )
                },
            ),
        )

        return []
    }),

    trackingEventCourtesyHold: actions.pure<Context, Events>((ctx, _ev) => {
        F.pipe(
            sequenceO({
                trackingId: O.fromNullable(ctx.checkoutTrackingId),
                isVsMember: O.some(ctx.vsCode.type == "valid"),
                checkoutStatus: O.fromPredicate(isAtLeastCheckoutStarted)(ctx.checkoutStatus),
            }),
            O.fold(
                () => {
                    const context = { trackingId: ctx.checkoutTrackingId, checkoutStatus: ctx.checkoutStatus }
                    logError(`Invalid tracking preconditions: ${ctx.checkoutStatus.status}`, context)
                },
                (s) => {
                    trackCourtesyHold(
                        {
                            trackingId: s.trackingId,
                            isVsMember: s.isVsMember,
                        },
                        s.checkoutStatus.response.bookingNumber,
                        s.checkoutStatus.response.validatedPrice.suitePayment,
                    )
                },
            ),
        )

        return []
    }),

    trackingEventPaymentConfirmed: actions.pure<Context, Events>((ctx, _ev) => {
        F.pipe(
            sequenceO({
                trackingId: O.fromNullable(ctx.paymentTrackingId),
                isVsMember: O.some(ctx.vsCode.type == "valid"),
                checkoutStatus: O.fromPredicate(isAtLeastCheckoutStarted)(ctx.checkoutStatus),
            }),
            O.map(paymentTrackingData),
            O.fold(
                () => {
                    const context = { trackingId: ctx.paymentTrackingId, checkoutStatus: ctx.checkoutStatus }
                    logError(`Invalid tracking preconditions: ${ctx.checkoutStatus.status}`, context)
                },
                (s) => {
                    trackPaymentConfirmed(s.trackingEvent, s.bookingNumber, s.suitePayment)
                },
            ),
        )
        return []
    }),

    trackingEventPaymentError: actions.pure<Context, Events>((ctx, ev) => {
        assertEvent(ev, "PAYMENT_ERROR")

        F.pipe(
            sequenceO({
                trackingId: O.fromNullable(ctx.paymentTrackingId),
                isVsMember: O.some(ctx.vsCode.type == "valid"),
                checkoutStatus: O.fromPredicate(isAtLeastCheckoutStarted)(ctx.checkoutStatus),
            }),
            O.map(paymentTrackingData),
            O.fold(
                () => {
                    const context = { trackingId: ctx.paymentTrackingId, checkoutStatus: ctx.checkoutStatus }
                    logError(`Invalid tracking preconditions: ${ctx.checkoutStatus.status}`, context)
                },
                (s) => {
                    trackPaymentError(s.trackingEvent, s.bookingNumber, s.suitePayment)
                },
            ),
        )

        return []
    }),

    trackingEventPaymentCancelled: actions.pure<Context, Events>((ctx, ev) => {
        assertEvent(ev, "PAYMENT_CANCELLED")

        F.pipe(
            sequenceO({
                trackingId: O.fromNullable(ctx.paymentTrackingId),
                isVsMember: O.some(ctx.vsCode.type == "valid"),
                checkoutStatus: O.fromPredicate(isAtLeastCheckoutStarted)(ctx.checkoutStatus),
            }),
            O.map(paymentTrackingData),
            O.fold(
                () => {
                    const context = { trackingId: ctx.paymentTrackingId, checkoutStatus: ctx.checkoutStatus }
                    logError(`Invalid tracking preconditions: ${ctx.checkoutStatus.status}`, context)
                },
                (s) => {
                    trackPaymentCancelled(s.trackingEvent, s.bookingNumber, s.suitePayment)
                },
            ),
        )

        return []
    }),
}
